import React, { useEffect, useState } from 'react'
import { Button, Card, Form } from 'react-bootstrap'
import { AnnotationDataBoundingBox } from '../../../types/models/annotation'
import SnippetItem from '../../snippets/SnippetItem'
import SnippetWithOverlay from '../../SnippetWithOverlay'
import { useAppSelector } from '../../../store/hooks'
import { selectSelectedProjectId } from '../../../store/slices/projectSlice'
import { useGetProjectQuery } from '../../../services/projectsApi'
import PillsClassification from '../../PillsClassification'
import { Tag } from '../../../types/models/tag'
import { useGetTagsQuery } from '../../../services/tagApi'
import { Bot, Edit3, Filter, CheckCircle2, XCircle, Eye } from 'lucide-react'
import {
  selectEditedBBoxIndexes,
  selectBoundingBoxStatuses,
  BBoxStatus,
} from '../../../store/slices/annotationSlice'

interface SnippetListProps {
  items: AnnotationDataBoundingBox[]
  contentId: string
  selectedBBoxClassIndex?: number | undefined
  showConfidence?: boolean
  showAcceptButton?: boolean
  showRejectButton?: boolean
  showInspectButton?: boolean
  onAccept?: (box: AnnotationDataBoundingBox, e: React.MouseEvent | KeyboardEvent) => void
  onReject?: (box: AnnotationDataBoundingBox, e: React.MouseEvent | KeyboardEvent) => void
  onInspect?: (box: AnnotationDataBoundingBox, e: React.MouseEvent | KeyboardEvent) => void
  hoveredBoxIndex?: number | undefined
  onHover?: (index: number | undefined) => void
  onSelect?: (box: AnnotationDataBoundingBox, index: number) => void
  // Selection support (used in AnnotationsReview export flow)
  selectionMode?: boolean
  selectedIndices?: number[]
  onToggleSelect?: (index: number) => void
  rotateLongSideVertical?: boolean
  layout?: 'grid' | 'list' // 'grid' for tiled layout, 'list' for compact list (default)
  projectAnnotationType?: string // Pass project annotation type to avoid refetching
  hideFilters?: boolean // Hide the filter bar (useful when parent already provides filtering via tabs)
  // Status filter (for AAL Review - controlled by parent)
  statusFilter?: 'all' | 'accepted' | 'rejected' | 'untouched'
  onStatusFilterChange?: (filter: 'all' | 'accepted' | 'rejected' | 'untouched') => void
}

const SnippetList: React.FC<SnippetListProps> = ({
  items,
  contentId,
  selectedBBoxClassIndex,
  showConfidence = true,
  showAcceptButton = false,
  showRejectButton = false,
  showInspectButton = false,
  onAccept,
  onReject,
  onInspect,
  hoveredBoxIndex,
  onHover,
  onSelect,
  selectionMode = false,
  selectedIndices = [],
  onToggleSelect,
  rotateLongSideVertical = false,
  layout = 'list', // Default to list for backward compatibility
  projectAnnotationType,
  hideFilters = false,
  statusFilter: externalStatusFilter,
  onStatusFilterChange,
}) => {
  const projectId = useAppSelector(selectSelectedProjectId)
  // Only fetch project if projectAnnotationType wasn't provided
  const { data: project } = useGetProjectQuery(projectId || '', {
    skip: !projectId || !!projectAnnotationType,
  })

  const { data: tags } = useGetTagsQuery(
    {
      project_id: projectId || '',
      dataset_id: project?.datasetid || '',
    },
    { skip: !projectId || !project?.datasetid }
  )

  useEffect(() => {
    if (selectedBBoxClassIndex !== undefined && selectedBBoxClassIndex !== null) {
      const el = document.getElementById(`prediction-snippet-${selectedBBoxClassIndex}`)
      if (el && typeof el.scrollIntoView === 'function') {
        try {
          el.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
        } catch {
          el.scrollIntoView()
        }
      }
    }
  }, [selectedBBoxClassIndex])

  // Use passed projectAnnotationType or fall back to fetched project data
  const annotationType = projectAnnotationType || project?.annotation_type
  const isBBox = annotationType === 'bounding_box' || annotationType === 'oriented_bounding_box'

  const editedBBoxIndexes = useAppSelector(selectEditedBBoxIndexes)
  const bboxStatuses = useAppSelector(selectBoundingBoxStatuses)

  // Filter state - use external if provided, otherwise use local state
  const [localStatusFilter, setLocalStatusFilter] = useState<
    'all' | 'accepted' | 'rejected' | 'untouched'
  >('all')
  const statusFilter = externalStatusFilter ?? localStatusFilter
  const setStatusFilter = onStatusFilterChange ?? setLocalStatusFilter

  // Filter items based on status
  const filteredItems = items.filter((box) => {
    if (statusFilter === 'all') return true
    const status = bboxStatuses[box.class_index]
    if (statusFilter === 'accepted') return status === BBoxStatus.Accepted
    if (statusFilter === 'rejected') return status === BBoxStatus.Rejected
    if (statusFilter === 'untouched') return status === BBoxStatus.Untouched || status === undefined
    return true
  })

  // Helper function to get status color
  const getStatusColor = (classIndex: number) => {
    const status = bboxStatuses[classIndex]
    if (status === BBoxStatus.Accepted) return '#10b981' // green
    if (status === BBoxStatus.Rejected) return '#ef4444' // red
    if (status === BBoxStatus.Untouched || status === undefined) return '#f59e0b' // orange
    return 'transparent'
  }

  // Helper function to get status label
  const getStatusLabel = (classIndex: number) => {
    const status = bboxStatuses[classIndex]
    if (status === BBoxStatus.Accepted) return 'Accepted'
    if (status === BBoxStatus.Rejected) return 'Rejected'
    if (status === BBoxStatus.Untouched || status === undefined) return 'Needs Review'
    return ''
  }

  // Render filter bar component
  const renderFilterBar = () => (
    <div
      className="d-flex gap-2 p-2 border-bottom"
      style={{
        borderColor: 'rgba(255, 255, 255, 0.1) !important',
        backgroundColor: '#161b22',
      }}
    >
      <Button
        size="sm"
        variant={statusFilter === 'all' ? 'primary' : 'outline-secondary'}
        onClick={() => setStatusFilter('all')}
        style={{ fontSize: '0.75rem' }}
      >
        <Filter size={12} className="me-1" />
        All ({items.length})
      </Button>
      <Button
        size="sm"
        variant={statusFilter === 'accepted' ? 'success' : 'outline-success'}
        onClick={() => setStatusFilter('accepted')}
        style={{ fontSize: '0.75rem' }}
      >
        <CheckCircle2 size={12} className="me-1" />
        Accepted
      </Button>
      <Button
        size="sm"
        variant={statusFilter === 'rejected' ? 'danger' : 'outline-danger'}
        onClick={() => setStatusFilter('rejected')}
        style={{ fontSize: '0.75rem' }}
      >
        <XCircle size={12} className="me-1" />
        Rejected
      </Button>
      <Button
        size="sm"
        variant={statusFilter === 'untouched' ? 'warning' : 'outline-warning'}
        onClick={() => setStatusFilter('untouched')}
        style={{ fontSize: '0.75rem' }}
      >
        <Eye size={12} className="me-1" />
        Review
      </Button>
    </div>
  )

  // Grid layout (tiled, larger snippets)
  if (layout === 'grid') {
    return (
      <div className="d-flex flex-column h-100">
        {/* Filter Bar - only show in AAL Review view */}
        {!hideFilters && renderFilterBar()}

        {/* Grid Items or Empty State */}
        {filteredItems.length === 0 ? (
          <div className="d-flex align-items-center justify-content-center h-100">
            <div className="text-center text-muted">
              <p className="mb-0">No items to display</p>
            </div>
          </div>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: '12px',
              padding: '8px',
              flex: 1,
              overflowY: 'auto',
            }}
          >
            {filteredItems.map((box, index) => {
              const isSelected = selectedBBoxClassIndex === box.class_index
              const isChecked = selectedIndices?.includes(index)
              const isHovered = hoveredBoxIndex === box.class_index
              const isEdited = editedBBoxIndexes.includes(box.class_index)
              const tagName =
                tags?.tags?.find((tag: Tag) => tag.id === box.class_name || tag.id === box.name)
                  ?.name ||
                box.class_name ||
                ''

              return (
                <div
                  key={box.class_index}
                  id={`prediction-snippet-${box.class_index}`}
                  className="d-flex flex-column"
                  style={{
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    backgroundColor:
                      selectionMode && isChecked
                        ? 'rgba(13, 110, 253, 0.25)'
                        : isSelected
                        ? 'rgba(13, 110, 253, 0.15)'
                        : 'transparent',
                    border:
                      selectionMode && isChecked
                        ? '2px solid #58a6ff'
                        : isSelected
                        ? '2px solid #58a6ff'
                        : isHovered
                        ? '1px solid rgba(88, 166, 255, 0.5)'
                        : '1px solid rgba(255, 255, 255, 0.1)',
                    // Only show colored status border in AAL Review view
                    borderLeft: hideFilters
                      ? undefined
                      : `4px solid ${getStatusColor(box.class_index)}`,
                    borderRadius: '8px',
                    overflow: 'hidden',
                    position: 'relative',
                  }}
                  onClick={() => {
                    if (selectionMode) {
                      onToggleSelect && onToggleSelect(index)
                    } else {
                      onSelect && onSelect(box, index)
                    }
                  }}
                  onMouseEnter={() => onHover && onHover(box.class_index)}
                  onMouseLeave={() => onHover && onHover(undefined)}
                >
                  {selectionMode && (
                    <div className="position-absolute top-2 start-2" style={{ zIndex: 10 }}>
                      <Form.Check
                        type="checkbox"
                        checked={!!isChecked}
                        onChange={() => {
                          /* Handled by onClick */
                        }}
                        onClick={(e) => {
                          e.stopPropagation()
                          onToggleSelect && onToggleSelect(index)
                        }}
                        aria-label="Select snippet"
                        readOnly
                      />
                    </div>
                  )}

                  {/* Thumbnail - Larger */}
                  {isBBox && (
                    <div
                      style={{
                        width: '100%',
                        aspectRatio: '1.15',
                        backgroundColor: '#0d1117',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                        overflow: 'hidden',
                        position: 'relative',
                      }}
                    >
                      <SnippetWithOverlay
                        projectAnnotationType={annotationType || ''}
                        contentId={contentId}
                        bbox={box}
                        isSelected={isSelected}
                        rotateLongSideVertical={rotateLongSideVertical}
                      />
                      {/* Edited indicator badge - only show in AAL Review view */}
                      {!hideFilters && editedBBoxIndexes.includes(box.class_index) && (
                        <div
                          className="position-absolute d-flex align-items-center justify-content-center"
                          style={{
                            top: '8px',
                            right: '8px',
                            width: '28px',
                            height: '28px',
                            backgroundColor: 'rgba(255, 165, 0, 0.9)',
                            borderRadius: '50%',
                            border: '2px solid #fff',
                            zIndex: 5,
                          }}
                          title="This box has been edited"
                        >
                          <Edit3 size={14} color="#fff" />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Info Section */}
                  <div className="p-3 d-flex flex-column flex-grow-1">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <span
                        className="text-light fw-semibold text-truncate"
                        style={{ fontSize: '0.875rem' }}
                      >
                        {tagName}
                      </span>
                      {/* Hide robot icon if box has been edited (no longer AI-labeled) */}
                      {box.ai_labeled && !isEdited && (
                        <Bot size={14} className="text-info flex-shrink-0" />
                      )}
                    </div>

                    {/* Status Badge - only show in AAL Review view */}
                    {!hideFilters && (
                      <div className="mb-2">
                        <span
                          className="badge"
                          style={{
                            backgroundColor: getStatusColor(box.class_index),
                            fontSize: '0.7rem',
                            fontWeight: '600',
                            padding: '4px 8px',
                          }}
                        >
                          {getStatusLabel(box.class_index)}
                        </span>
                      </div>
                    )}

                    {/* Hide confidence if box has been edited (no longer AI prediction) */}
                    {showConfidence && box.confidence !== undefined && !isEdited && (
                      <div
                        className="mb-2"
                        style={{ fontSize: '0.8125rem', color: 'rgba(255, 255, 255, 0.7)' }}
                      >
                        {Math.round(box.confidence * 100)}% confidence
                      </div>
                    )}

                    {/* Actions */}
                    {((showAcceptButton && onAccept) ||
                      (showRejectButton && onReject) ||
                      (showInspectButton && onInspect)) && (
                      <div className="d-flex gap-2 mt-auto">
                        {showAcceptButton && onAccept && (
                          <Button
                            variant="outline-success"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation()
                              onAccept(box, e)
                            }}
                            className="flex-grow-1"
                            style={{ fontSize: '0.75rem' }}
                          >
                            ✓ Accept
                          </Button>
                        )}
                        {showRejectButton && onReject && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation()
                              onReject(box, e)
                            }}
                            className="flex-grow-1"
                            style={{ fontSize: '0.75rem' }}
                          >
                            ✗ Reject
                          </Button>
                        )}
                        {showInspectButton && onInspect && (
                          <Button
                            variant="outline-warning"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation()
                              onInspect(box, e)
                            }}
                            className="flex-grow-1"
                            style={{ fontSize: '0.75rem' }}
                          >
                            👁 Inspect
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    )
  }

  // List layout (compact, original style)
  return (
    <div className="d-flex flex-column h-100">
      {/* Filter Bar - only show in AAL Review view */}
      {!hideFilters && renderFilterBar()}

      {/* List Items or Empty State */}
      {filteredItems.length === 0 ? (
        <div className="d-flex align-items-center justify-content-center h-100">
          <div className="text-center text-muted">
            <p className="mb-0">No items to display</p>
          </div>
        </div>
      ) : (
        <div className="d-flex flex-column gap-1 flex-1 overflow-auto">
          {filteredItems.map((box, index) => {
            const isSelected = selectedBBoxClassIndex === box.class_index
            const isChecked = selectedIndices?.includes(index)
            const isHovered = hoveredBoxIndex === box.class_index
            const isEdited = editedBBoxIndexes.includes(box.class_index)
            const tagName =
              tags?.tags?.find((tag: Tag) => tag.id === box.class_name || tag.id === box.name)
                ?.name ||
              box.class_name ||
              ''

            return (
              <div
                key={box.class_index}
                id={`prediction-snippet-${box.class_index}`}
                className={`d-flex align-items-center p-2 ${
                  selectionMode && isChecked
                    ? 'bg-primary bg-opacity-25 border-start border-primary border-3'
                    : isSelected
                    ? 'bg-primary bg-opacity-10 border-start border-primary border-3'
                    : isHovered
                    ? 'bg-secondary bg-opacity-10'
                    : ''
                }`}
                style={{
                  cursor: 'pointer',
                  transition: 'all 0.1s ease',
                  // Only show colored status border in AAL Review view
                  borderLeft: hideFilters
                    ? undefined
                    : (selectionMode && isChecked) || isSelected
                    ? '3px solid'
                    : `3px solid ${getStatusColor(box.class_index)}`,
                }}
                onClick={() => {
                  if (selectionMode) {
                    onToggleSelect && onToggleSelect(index)
                  } else {
                    onSelect && onSelect(box, index)
                  }
                }}
                onMouseEnter={() => onHover && onHover(box.class_index)}
                onMouseLeave={() => onHover && onHover(undefined)}
              >
                {selectionMode && (
                  <div className="me-2">
                    <Form.Check
                      type="checkbox"
                      checked={!!isChecked}
                      onChange={() => {
                        /* Handled by onClick */
                      }}
                      onClick={(e) => {
                        e.stopPropagation()
                        onToggleSelect && onToggleSelect(index)
                      }}
                      aria-label="Select snippet"
                      readOnly
                    />
                  </div>
                )}

                {/* Thumbnail */}
                {isBBox && (
                  <div
                    className="flex-shrink-0 me-2 position-relative"
                    style={{
                      width: '75px',
                      height: '75px',
                      backgroundColor: '#0d1117',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '4px',
                      overflow: 'hidden',
                    }}
                  >
                    <SnippetWithOverlay
                      projectAnnotationType={project?.annotation_type || ''}
                      contentId={contentId}
                      bbox={box}
                      isSelected={isSelected}
                      rotateLongSideVertical={rotateLongSideVertical}
                    />
                    {/* Edited indicator badge - only show in AAL Review view */}
                    {!hideFilters && editedBBoxIndexes.includes(box.class_index) && (
                      <div
                        className="position-absolute d-flex align-items-center justify-content-center"
                        style={{
                          top: '4px',
                          right: '4px',
                          width: '20px',
                          height: '20px',
                          backgroundColor: 'rgba(255, 165, 0, 0.9)',
                          borderRadius: '50%',
                          border: '1.5px solid #fff',
                          zIndex: 5,
                        }}
                        title="This box has been edited"
                      >
                        <Edit3 size={10} color="#fff" />
                      </div>
                    )}
                  </div>
                )}

                {/* Info */}
                <div className="flex-grow-1 min-w-0">
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <span className="text-light small fw-semibold text-truncate">{tagName}</span>
                    {/* Hide robot icon if box has been edited (no longer AI-labeled) */}
                    {box.ai_labeled && !isEdited && (
                      <Bot size={12} className="text-info flex-shrink-0" />
                    )}
                    {/* Status Badge - only show in AAL Review view */}
                    {!hideFilters && (
                      <span
                        className="badge"
                        style={{
                          backgroundColor: getStatusColor(box.class_index),
                          fontSize: '0.65rem',
                          fontWeight: '600',
                          padding: '2px 6px',
                        }}
                      >
                        {getStatusLabel(box.class_index)}
                      </span>
                    )}
                  </div>
                  {/* Hide confidence if box has been edited (no longer AI prediction) */}
                  {showConfidence && box.confidence !== undefined && !isEdited && (
                    <div style={{ fontSize: '0.8125rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                      {Math.round(box.confidence * 100)}% confidence
                    </div>
                  )}
                </div>

                {/* Actions */}
                {((showAcceptButton && onAccept) ||
                  (showRejectButton && onReject) ||
                  (showInspectButton && onInspect)) && (
                  <div className="d-flex gap-1 flex-shrink-0 ms-2">
                    {showAcceptButton && onAccept && (
                      <Button
                        variant="outline-success"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation()
                          onAccept(box, e)
                        }}
                        style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                      >
                        ✓
                      </Button>
                    )}
                    {showRejectButton && onReject && (
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation()
                          onReject(box, e)
                        }}
                        style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                      >
                        ✗
                      </Button>
                    )}
                    {showInspectButton && onInspect && (
                      <Button
                        variant="outline-warning"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation()
                          onInspect(box, e)
                        }}
                        style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                        title="Mark as needs inspection"
                      >
                        👁
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default SnippetList
